"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useOpenBillingPortal = useOpenBillingPortal;
var react_1 = require("react");
var billing_1 = require("../features/apis/billing");
var client_scoped_query_1 = require("../utils/client-scoped-query");
/**
 * Opens the Stripe Customer Portal in a new tab. `onReturn` runs when the user comes back to this
 * tab, since changes made in the portal (new card, paid invoice) don't reach this page otherwise.
 */
function useOpenBillingPortal(returnTo, onReturn) {
    var _a;
    var clientId = (_a = (0, client_scoped_query_1.useActiveClientId)()) !== null && _a !== void 0 ? _a : "";
    var _b = (0, billing_1.useCreatePortalSessionMutation)(), createPortalSession = _b[0], isLoading = _b[1].isLoading;
    // Blocks a second click before React re-renders with `isLoading`.
    var isOpeningRef = (0, react_1.useRef)(false);
    var onReturnRef = (0, react_1.useRef)(onReturn);
    var focusListenerRef = (0, react_1.useRef)(null);
    // Bumped on unmount so a portal session that resolves afterwards doesn't touch this page.
    var requestTokenRef = (0, react_1.useRef)(0);
    var _c = (0, react_1.useState)(""), error = _c[0], setError = _c[1];
    onReturnRef.current = onReturn;
    (0, react_1.useEffect)(function () {
        return function () {
            requestTokenRef.current += 1;
            if (focusListenerRef.current)
                window.removeEventListener("focus", focusListenerRef.current);
        };
    }, []);
    var listenForReturn = function () {
        if (focusListenerRef.current)
            return;
        var handleFocus = function () {
            var _a;
            window.removeEventListener("focus", handleFocus);
            focusListenerRef.current = null;
            (_a = onReturnRef.current) === null || _a === void 0 ? void 0 : _a.call(onReturnRef);
        };
        focusListenerRef.current = handleFocus;
        window.addEventListener("focus", handleFocus);
    };
    var open = function () {
        if (isOpeningRef.current || !clientId)
            return;
        // Must open synchronously inside the click: browsers block window.open after an await.
        var tab = window.open("", "_blank");
        if (!tab) {
            setError("Your browser blocked the billing tab. Allow pop-ups for this site and try again.");
            return;
        }
        tab.opener = null;
        tab.document.title = "Opening billing portal…";
        tab.document.body.textContent = "Opening the billing portal…";
        isOpeningRef.current = true;
        setError("");
        var requestToken = requestTokenRef.current;
        createPortalSession({ returnTo: returnTo, clientId: clientId })
            .unwrap()
            .then(function (response) {
            // The tab is the user's now; send it on even if they've left this page.
            tab.location.href = response.content.url;
            if (requestToken === requestTokenRef.current)
                listenForReturn();
        })
            .catch(function () {
            tab.close();
            if (requestToken === requestTokenRef.current)
                setError("We couldn't open the billing portal. Please try again.");
        })
            .finally(function () {
            isOpeningRef.current = false;
        });
    };
    return { open: open, isOpening: isLoading, error: error };
}
